/**
 * Notification Types
 */

export type NotificationType =
    | 'MEETING_ANALYZED'
    | 'QUOTE_REQUESTED'
    | 'QUOTE_READY'
    | 'QUOTE_STATUS_CHANGED'
    | 'OPPORTUNITY_STAGE_CHANGE'
    | 'PROMOTION_EXPIRING'
    | 'STALE_OPPORTUNITY'
    | 'SYSTEM_ANNOUNCEMENT'
    | 'PROPOSAL_UPDATED'
    | 'PROPOSAL_VIEWED'
    | 'PROPOSAL_APPROVED'
    | 'PROPOSAL_COMMENT';

export interface Notification {
    id: string;
    user_id: string;
    type: NotificationType;
    title: string;
    message: string;
    entity_type: string | null;
    entity_id: string | null;
    is_read: boolean;
    read_at: string | null;
    created_at: string;
}

export interface NotificationPreference {
    type: NotificationType;
    in_app_enabled: boolean;
    email_enabled: boolean;
    slack_enabled?: boolean;
    granularity?: 'every' | 'first' | null;
}

export interface NotificationPreferencesMap {
    [key: string]: NotificationPreference;
}

// For updates where fields are optional
export interface NotificationPreferenceUpdate {
    in_app_enabled?: boolean;
    email_enabled?: boolean;
    slack_enabled?: boolean;
    granularity?: 'every' | 'first' | null;
}

export interface NotificationPreferencesUpdateMap {
    [key: string]: NotificationPreferenceUpdate;
}

export interface NotificationTypeInfo {
    type: NotificationType;
    defaultTitle: string;
    entityType: string | null;
    roles: string[];
}

// Response types
export interface NotificationsResponse {
    notifications: Notification[];
    total: number;
    hasMore: boolean;
}

export interface UnreadCountResponse {
    count: number;
}

export interface NotificationPreferencesResponse {
    preferences: NotificationPreferencesMap;
}

export interface NotificationTypesResponse {
    types: NotificationTypeInfo[];
}

// Human-readable labels for notification types
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
    MEETING_ANALYZED: 'Meeting Analysis Complete',
    QUOTE_REQUESTED: 'Quote Requested',
    QUOTE_READY: 'Quote Ready',
    QUOTE_STATUS_CHANGED: 'Quote Status Changed',
    OPPORTUNITY_STAGE_CHANGE: 'Opportunity Stage Change',
    PROMOTION_EXPIRING: 'Promotion Expiring',
    STALE_OPPORTUNITY: 'Stale Opportunity',
    SYSTEM_ANNOUNCEMENT: 'System Announcement',
    PROPOSAL_UPDATED: 'New pricing — update your proposal',
    PROPOSAL_VIEWED: 'Client opened your proposal',
    PROPOSAL_APPROVED: 'Client approved your proposal',
    PROPOSAL_COMMENT: 'Client commented on your proposal',
};

// SVG icon paths for notification types (Heroicons outline, used via <svg><path d="..."/></svg>)
export const NOTIFICATION_TYPE_ICONS: Record<NotificationType, string> = {
    MEETING_ANALYZED: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    QUOTE_REQUESTED: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    QUOTE_READY: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    QUOTE_STATUS_CHANGED: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    OPPORTUNITY_STAGE_CHANGE: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    PROMOTION_EXPIRING: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    STALE_OPPORTUNITY: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z',
    SYSTEM_ANNOUNCEMENT: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    PROPOSAL_UPDATED: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    PROPOSAL_VIEWED: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
    PROPOSAL_APPROVED: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    PROPOSAL_COMMENT: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
};

// Routes for navigating to related entities
export function getNotificationRoute(notification: Notification): string | null {
    const { entity_type, entity_id } = notification;

    if (!entity_type || !entity_id) {
        return null;
    }

    switch (entity_type) {
        case 'meeting':
            return `/advisor/meetings/${entity_id}`;
        case 'quote':
            return `/advisor/quotes/${entity_id}`;
        case 'opportunity':
            return `/advisor/opportunities/${entity_id}`;
        case 'promotion':
            return `/advisor/promotions/${entity_id}`;
        // Supplier-side MDF notifications
        case 'mdf_offering':
            return '/supplier/mdf/offerings';
        case 'mdf_request':
            return `/supplier/mdf/requests/${entity_id}`;
        // Advisor-side MDF notifications
        case 'advisor_mdf_offering':
            return `/advisor/mdf/marketplace/${entity_id}`;
        case 'advisor_mdf_request':
            return `/advisor/mdf/requests/${entity_id}`;
        // Sweep deal (e.g. new pricing landed — update the proposal)
        case 'sweep_deal':
            return `/advisor/sweep/deals/${entity_id}`;
        default:
            return null;
    }
}
